// index.tsx
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Container, Box, Grid, Typography } from '@mui/material';
import { PageProps } from '../../../types/navigationProperties';
import { getProject, likeProject, Project } from '../../../services/ProjectsServiceProvider';
import { displayErrorToast, displayMessageToast } from '../../../utils/toasts';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components/PageHeader';
import Footer from '../../../components/Footer';
import LikeIconButton from '../../../components/LikeIconButton';
import { LightShotSingle } from '../../../components/LightShotSingle';
import { GalleryImage } from '../../../types/galleryImage';
import LoaderSpinner from '../../../components/LoaderSpinner';
import { SetButtonCallbackFunction } from '../../../types/callback';
import { SEO } from '../../../components/SEO';
import { MetaTag, Meta } from '../../../services/SEOServiceProvider';
import aboutPage from '../../../assets/img/about_page.jpg';
import { ColorfulText } from '../../../components/ColorfulText';


export default function ProjectDetails({ props, setPageName, imageSize, metaTags }: { props: PageProps, setPageName: SetButtonCallbackFunction, imageSize: string, metaTags: MetaTag[] }) {
    const { slug } = useParams();
    const { t } = useTranslation();
    const language = props.language;
    const [project, setProject] = React.useState<Project>();
    const [open, setOpen] = React.useState<boolean>(false);
    const [selectedImage, setSelectedImage] = React.useState<GalleryImage>();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [likeLoading, setLikeLoading] = React.useState<boolean>(false);
    const [metaTag, setMetaTag] = React.useState<MetaTag>();

    React.useEffect(() => {
        setPageName('project-details');
        let metaTagData = metaTags !== undefined && metaTags.filter((item: MetaTag) => item.page === 'project-details' );
        if(metaTagData && metaTagData.length > 0) {
            let projectDetailsPageMetaTag: MetaTag = {...metaTagData[0]} as MetaTag;
            projectDetailsPageMetaTag.url = window.location.href;
            projectDetailsPageMetaTag.image = aboutPage;
            setMetaTag(projectDetailsPageMetaTag);
        }

        if(slug !== undefined) {
            (async () => {
                try {
                    setLoading(true);
                    const projectData = await getProject(slug, imageSize);
                    console.log(projectData);
                    setProject(projectData);
                    if(metaTagData && metaTagData.length > 0) {
                        let projectDetailsPageMetaTag: MetaTag = {...metaTagData[0]} as MetaTag;
                        let projectNameMeta: Meta = { key: 'name', value: projectData.title.value };
                        projectDetailsPageMetaTag.url = window.location.href;
                        projectDetailsPageMetaTag.image = aboutPage;
                        projectDetailsPageMetaTag.title = projectData.title.value;
                        projectDetailsPageMetaTag.metas = [...[projectNameMeta]];
                        setMetaTag(projectDetailsPageMetaTag);
                    }
                } catch(error) {
                    displayErrorToast(error);
                } finally {
                    setLoading(false);
                }
            })();
        } else {
            displayErrorToast('Unknown application error!');
        }
    }, [language, slug, setPageName, imageSize, metaTags]);

    const projectLiked = (project: Project) => {
        (async () => {
            try {
                setLikeLoading(true);
                const projectLikedData = await likeProject(project);
                console.log(projectLikedData);
                displayMessageToast(`${t('project.like.message')} ${project.title}!`);
                setProject(projectLikedData);
            } catch(error) {
                displayErrorToast(error);
            } finally {
                setLikeLoading(false);
            }
        })()
    }

    const handleOpenLightShot = (image: string, name: string) => {
        let item: GalleryImage = { src: image, name: name };
        setSelectedImage(item);
        setOpen(true);
    }

    const headingProps = {
        title: `${t("projects.title")} ${project !== undefined ? project.title.value : ''}`,
        description: project !== undefined ? project.short_description.value : undefined
    };

    return loading || project === undefined ? (
        <div>
            <SEO metaTag={metaTag} />
            <LoaderSpinner loading={loading} />
        </div>
    ) : (
        <Box>
            <SEO metaTag={metaTag} />
            <LightShotSingle image={selectedImage} opened={open} setOpenLightShot={setOpen} />
            <Box>
                <LikeIconButton object={project} likeLoading={likeLoading} likeFunction={projectLiked} />
            </Box>
            <Box>
                <PageHeader props={headingProps} />
            </Box>
            <Box sx={{ bgcolor: 'primary.light' }}>
                <Container maxWidth="lg" sx={{ paddingTop: { xs: '20px', sm: '20px', md: '40px' } }}>
                    <ColorfulText style={{
                            textAlign: 'center',
                            fontSize: { sm: '14px', md: '16px', lg: '18px' } }}
                                  text={project.long_description.value}
                                  randomChars={true} skipChar={[]} useColor={'random'} />
                </Container>
            </Box>
            <Box sx={{ py: '40px', px: '20px', paddingTop: { xs: '20px', sm: '20px', md: '40px', lg: '60px' }, bgcolor: 'primary.light' }}
                 style={{ width: '100%', height: '100%' }}>
                <Grid container spacing={4} sx={{ mb: 2, width: '100%' }}>
                    {project !== undefined && project.sections !== undefined && project.sections.map((item, i) => item.ltr ? (
                        <Box sx={{ width: '100%', display: { xs: 'block', md: 'flex' } }} justifyContent="flex-start" key={i}>
                            <Box sx={{ width: '100%', maxWidth: '550px' }}>
                                <img loading="lazy"
                                     src={item.image}
                                     style={{
                                        width: '100%',
                                        maxWidth: '550px',
                                        float: 'left',
                                     }}
                                     alt={item.title.value}
                                     onClick={() => handleOpenLightShot(item.image, item.title.value)} />
                            </Box>
                            <Box sx={{ width: '100%', my: 'auto' }}>
                                <Box sx={{ marginLeft: { xs: '0px', md: '40px', lg: '60px' } }}>
                                    <Typography sx={{
                                                fontSize: { sm: '14px', md: '16px', lg: '18px' },
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                paddingBottom: '16px'
                                            }}>
                                        {item.title.value}
                                    </Typography>
                                    {item.short_description.value !== undefined && item.short_description.value !== '' ? (
                                        <Typography sx={{
                                                        whiteSpace: "pre-wrap",
                                                        paddingBottom: '16px'
                                                   }}>
                                            { item.short_description.value }
                                        </Typography>
                                    ): ( <></>)}
                                    <Typography sx={{ whiteSpace: "pre-wrap" }}>{ item.long_description.value }</Typography>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%', display: { xs: 'block', md: 'flex' } }} justifyContent="flex-end" key={i}>
                            <Box sx= {{ my: 'auto' }} >
                                <Box sx={{ marginRight: { xs: '0px', md: '40px', lg: '60px' } }}>
                                    <Typography sx={{
                                                fontSize: { sm: '14px', md: '16px', lg: '18px' },
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                paddingBottom: '16px'
                                              }}>{item.title.value}
                                    </Typography>
                                    {item.short_description.value !== undefined && item.short_description.value !== '' ? (
                                        <Typography sx={{
                                                        whiteSpace: "pre-wrap",
                                                        paddingBottom: '16px'
                                                     }}>
                                            { item.short_description.value }
                                        </Typography>
                                    ): ( <></>)}
                                    <Typography sx={{ whiteSpace: "pre-wrap" }}>{ item.long_description.value }</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ width: '100%', maxWidth: '550px' }}>
                                <img loading="lazy"
                                     src={item.image}
                                     style={{
                                        width: '100%',
                                        maxWidth: '550px',
                                        float: 'right',
                                     }}
                                     alt={item.title.value}
                                     onClick={() => handleOpenLightShot(item.image, item.title.value)} />
                            </Box>
                        </Box>
                    ))}
                </Grid>
            </Box>
            <Footer {...props} />
        </Box>
    );
}


